import * as THREE from 'three'

import GlassShape from './../glassShape'
import GlassSegment from '@/model/glassSegment'
import MathsUtils from '@/utils/mathsUtils'

import Types from '../../types'
import { AlignmentTypes } from '@/store/mathTypes'


/*
 * SemiCircleShape
 * Semi Circle glass shape with radius
 */
export default class SemiCircleShape extends GlassShape {
  constructor(editor, offsetPosition = null, itemData = {}) {
    super(editor, itemData)
    this.objectClassName = 'SemiCircleShape'
    
    this.shapeType = Types.LibraryItemShapeTypes.semiCircle
    this.defaultRadius = MathsUtils.roundNumber(parseFloat(itemData.radius || 500), 1)
    this.radius = this.defaultRadius
    this.segmentListEditable = true

    this.reBuildShape(offsetPosition)
  }


  /**
   * Rebuilds the shape segments based on the basic width/height of the rectangle
   * Only to be used when first creating the shape
   */
  reBuildShape(offsetPosition) {
    var startX = offsetPosition ? offsetPosition.x : 0.0
    var startY = offsetPosition ? offsetPosition.y : 0.0

    var radius = Math.max(this.radius, 1)

    this.segmentList = []

    // 1 180 arcs, 1 straight line
    let segmentA = new GlassSegment(Types.SegmentTypes.arc, {
      arcRadius: radius,
      arcStartAngle: 0,
      arcEndAngle: 180,
      arcClockwise: true
    })
    segmentA.arcCenterPosition.set(startX + radius, startY + radius)
    segmentA.arcUpdated()
    this.segmentList.push(segmentA)

    let segmentB = new GlassSegment(Types.SegmentTypes.line, {
      startPosition: new THREE.Vector2(startX, startY + radius),
      endPosition: new THREE.Vector2(startX + radius * 2, startY + radius),
    })
    this.segmentList.push(segmentB)
  }


  /**
   * Resize the shape
   * Handled by the actual shape classes to rebuild the shape
   */
  resizeShape(radius) {
    let bounds = this.calculateSegmentBounds()
    if(bounds.isEmpty())
      return

    this.radius = radius

    this.reBuildShape(bounds.min)
  }


  /**
   * Calculates the default panel item position 2D coordinate 
   * Used for panel items automatically added to the shape via applyDefaultPanelItems
   * Returns 2D position of the center of the panel item
   */
  calculateDefaultPanelItemPosition(alignHorz, alignVert, itemWidth, itemHeight) {
    let shapeBounds = this.calculateSegmentBounds()
    let center = shapeBounds.getCenter(new THREE.Vector2())
    let posDir = new THREE.Vector2(0, 0)

    let itemRadius = Math.sqrt((itemWidth/2 * itemWidth/2) + (itemHeight/2 * itemHeight/2))
    switch(alignHorz) {
      case AlignmentTypes.left:
        posDir.x = -1
        break
      case AlignmentTypes.center:
        posDir.x = 0
        break
      case AlignmentTypes.right:
        posDir.x = 1
        break
    }
    switch(alignVert) {
      case AlignmentTypes.top:
        posDir.y = -1
        break
      case AlignmentTypes.middle:
        posDir.y = 0
        break
      case AlignmentTypes.bottom:
        posDir.y = 0
        break
    }

    posDir.normalize()
    return center.addScaledVector(posDir, this.radius - itemRadius)
  }

  // Clone the circle shape
  clone() {
    let newShape = new SemiCircleShape(this.editor, null, this.itemData)

    newShape.copy(this)
    return newShape
  }

  // Copy the circle shape to this shape
  copy(otherShape) {
    super.copy(otherShape)
    
    this.defaultRadius = otherShape.defaultWidth
    this.radius = otherShape.width
  }


  // Serialize/Deserialize to/from a JSON object
  serialize() {
    var outputData = super.serialize()

    outputData.defaultRadius = this.defaultRadius
    outputData.radius = this.radius

    return outputData
  }
  deserialize(object) {
    super.deserialize(object)

    this.defaultRadius = parseFloat(object.defaultRadius || 0)
    this.radius = parseFloat(object.radius || 0)
  }


  /**
   * Check if the layout is still a circle shape
   * Used for special resizing of specific shapes
   */
  isLayoutSemiCircleShape() {
    // Check check if the segment list is 1 arcs & 1 straight line
    // Not 100% accurate, but good enough
    return (this.segmentList.length == 2 && this.segmentList.some(segment => segment.type == Types.SegmentTypes.arc) && this.segmentList.some(segment => segment.type == Types.SegmentTypes.line))
  }
}
